import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import classNames from "classnames";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Module, ModuleCaption } from "Components/Module";
import { SpinningButton } from "Components/ReactstrapSlim";
import { InputFL, SelectInputFL, ValidationSummary } from "Components/Inputs";
import { AccountClient, CountriesClient, CountryCode, IMemberDataModel, MemberDataModel, StateCode } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import formatPhoneNumber from "Utils/formatPhoneNumber";

const IMemberDataModelSchema: yup.SchemaOf<IMemberDataModel> = yup.object({
   firstName: yup.string().trim().required("First name is required").defined(),
   lastName: yup.string().trim().required("Last name is required").defined(),
   company: yup.string().defined().nullable(),
   title: yup.string().defined().nullable(),
   phoneNumber: yup.string().phone("Phone number is not valid").defined().nullable(),
   postalCode: yup.string().defined().nullable(),
   address: yup.string().defined().nullable(),
   city: yup.string().defined().nullable(),
   state: yup.string().defined().nullable(),
   country: yup.string().defined().nullable()
}).defined();

export interface PersonalInformationProps {
}

export const PersonalInformation: React.FC<PersonalInformationProps> = () => {
   const accountClient = useApiClient(AccountClient);
   const countryClient = useApiClient(CountriesClient);
   const dispatch = useDispatch();
   const [isMemberDataLoading, setIsMemberDataLoading] = useState<boolean>(true);
   const [_isDictionaryDataLoading, setIsDictionaryDataLoading] = useState<boolean>(true);
   const [isSaving, setIsSaving] = useState<boolean>(false);
   const [serverErrors, setServerErrors] = useState<Array<[string, string]>>([]);
   const [countryCodes, setCountryCodes] = useState<CountryCode[]>([])
   const [stateCodes, setStateCodes] = useState<StateCode[]>([]);  

   const formMethods = useForm<IMemberDataModel>({
      mode: "onTouched",
      resolver: yupResolver(IMemberDataModelSchema)      
   })

   const submit = async () => {
      if (isSaving)
         return;
      setIsSaving(true);
      setServerErrors([]);
      try {
         const isValid = await formMethods.trigger();
         if (isValid) {
            const formValues = formMethods.getValues();
            const changeResult = await accountClient.updatePersonalInformation(MemberDataModel.fromJS(formValues));
            if (changeResult.success) {
               dispatch(LSStore.refreshUserThunk());
            } else {
               if (changeResult.validationErrors && changeResult.validationErrors.length)
                  setServerErrors(changeResult.validationErrors.map(err => ["", err]));
               else
                  setServerErrors([["", "Unexpected server error"]]);
            }
         }
      } catch (err: any) {
         if (err.error) {
            setServerErrors([["", err.error]]);
         }
         console.log(err);
      } finally {
         setIsSaving(false);
      }
   }

   const loadMemberData = async (abortSignal: AbortSignal) => {
      setIsMemberDataLoading(true)
      try {
         const memberData = await accountClient.personalInformation(abortSignal);

         if (memberData.phoneNumber) {
            memberData.phoneNumber = await formatPhoneNumber(memberData.phoneNumber, memberData.country);
         }         
         formMethods.reset(memberData);
      }
      catch(ex) { 
         console.log("Error loading member profile", ex);         
      }
      setIsMemberDataLoading(false);
   }

   const loadDictionaryData = async (abortSignal: AbortSignal) => {
      try {
         const result = await countryClient.listWithStates(true, abortSignal);
         setCountryCodes(result);         
      } catch (err) {
         console.log(err);
      } finally {
         setIsDictionaryDataLoading(false);
      }
   }

   const country = formMethods.watch("country");

   const isCountryWithStates = useMemo(() => {
      return (
         !!country
         && !!countryCodes.find(
            (c) => c.value == country && c.stateCodes.length > 0
         )
      );
   }, [country]);

   useEffect(() => {
      const codes = countryCodes.find(cc => cc.value == country)?.stateCodes;
      if (codes) {
         setStateCodes(codes);
         formMethods.setValue("state", codes.length > 0 ? codes[0].value : undefined);
      }
   }, [country])
   
   useEffect(() => {
      const abortController = new AbortController();
      loadMemberData(abortController.signal);
      loadDictionaryData(abortController.signal);

      return function cleanup() {
         abortController.abort();
      }
   }, []);

   return (
      <Module size="lg" loading={isMemberDataLoading} className={classNames("module-personal-information")}>
         <ModuleCaption className="mb-px8">
            Personal Information
         </ModuleCaption>
         <div className="personal-information-wrapper">
            <ValidationSummary errors={formMethods.formState.errors} serverErrors={serverErrors} />
            <InputFL control={formMethods.control} name="firstName" className="field" placeholder="First name" bsSize={"lg"} />
            <InputFL control={formMethods.control} name="lastName" className="field" placeholder="Last name" bsSize={"lg"} />
            <InputFL control={formMethods.control} name="company" className="field" placeholder="Company" bsSize={"lg"} />
            <InputFL control={formMethods.control} name="title" className="field" placeholder="Title" bsSize={"lg"} />
            <InputFL control={formMethods.control} name="phoneNumber" className="field" placeholder="Phone number" bsSize={"lg"} />
            <InputFL control={formMethods.control} name="address" className="field" placeholder="Address" bsSize={"lg"} />
            <InputFL control={formMethods.control} name="city" className="small" placeholder="City" bsSize={"lg"} />
            {
               isCountryWithStates ? <SelectInputFL control={formMethods.control} name="state" className="small" placeholder="State" bsSize={"lg"} >
                  {stateCodes.map(c => <option key={c.value} value={c.value}>{c.text}</option>)}
               </SelectInputFL>
               : <InputFL control={formMethods.control} name="state" className="small" placeholder="State" bsSize={"lg"} />
            }
            <SelectInputFL control={formMethods.control} name="country" className="small" placeholder="Country" bsSize={"lg"} >
               {countryCodes.map(c => <option key={c.value} value={c.value}>{c.text}</option>)}
            </SelectInputFL>
            <InputFL control={formMethods.control} name="postalCode" className="small" placeholder="Zip / Postal code" bsSize={"lg"} />
            <div className="btn-row">
               <SpinningButton isLoading={isSaving} onClick={() => submit()} color="primary" size="lg" fluid="sm" className="scpf-cta">Save Changes</SpinningButton>
            </div>
         </div>
      </Module>
   );
}